import { WxAPI } from '@metoceanapi/wxtiles-mbox/src/wxAPI/WxAPI';
import type { WxDataSetManager } from '@metoceanapi/wxtiles-mbox/src/wxAPI/WxDataSetManager';
import type { WxLayerOptions } from '@metoceanapi/wxtiles-mbox/src/wxlayer/wxlayer';
import { WxTileSource } from '@metoceanapi/wxtiles-mbox/src/wxsource/wxsource';
import type { FrameworkOptions } from '@metoceanapi/wxtiles-mbox/src/wxsource/wxsourcetypes';
import { type IStage, metIntegration } from '@metservicedev/platforms-services-interface';
import { toHash } from '@mono/to-hash';
import { useEffect, useState } from 'react';

interface wxtilesV3Boundary {
    east: number;
    north: number;
    south: number;
    west: number;
}

export interface wxtilesv3Dataset {
    instances: {
        [instanceTime: string]: {
            times: string[];
            variables: string[];
            maxZoom: number;
            boundaries: {
                boundaries180: [wxtilesV3Boundary, wxtilesV3Boundary];
                boundaries360: [wxtilesV3Boundary];
                boundariesnorm: wxtilesV3Boundary;
            };
            variablesMeta: {
                [variableName: string]: {
                    units: string;
                };
            };
        };
    };
}

interface useWXtilesV3Props {
    datasetsToExpand?: string[];
    stage: IStage;
    refreshTime?: number;
}

export const useWxtilesV3 = ({ datasetsToExpand, stage, refreshTime }: useWXtilesV3Props): useWXtilesV3Return => {
    const [wxDatasetManagers, setWxDatasetManagers] = useState<Record<string, WxDataSetManager>>({});
    const [variables, setVariables] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [datasets, setDatasets] = useState<Record<string, wxtilesv3Dataset>>({});
    const [variableIndexedDatasets, setVariableIndexedDatasets] = useState<Record<string, string[]>>({});

    const [wxapi, setWxapi] = useState<WxAPI>();
    const [tick, setTick] = useState('put inspiring quote here');

    useEffect(() => {
        if (refreshTime === undefined) return;

        const interval = setInterval(() => {
            setTick((prev) => toHash(prev));
        }, refreshTime);
        return () => clearInterval(interval);
    }, [refreshTime]);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const wxapi = new WxAPI({
                dataServerURL: `${metIntegration.wxtilesv3[stage]}/data/`,
                requestInit: {
                    headers: { authorization: `Bearer ${JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')?.accessToken?.accessToken}` },
                },
                maskURL: 'auto',
                qtreeURL: 'auto',
            });

            const variables = Array.from(
                new Set(
                    (
                        await Promise.all(
                            (await wxapi.getAllDatasetsNames()).map(async (datasetName) => (await wxapi.getDatasetAllVariables(datasetName)) ?? [])
                        )
                    ).flat()
                )
            );

            const datasetsResponse = await fetch(`${metIntegration.wxtilesv3[stage]}/api/datasets?expanded=true`, {
                headers: { authorization: `Bearer ${JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')?.accessToken?.accessToken}` },
            });
            const datasets = (await datasetsResponse.json()) as Record<string, wxtilesv3Dataset>;

            setDatasets(datasets);
            setWxapi(wxapi);
            setVariables(variables);
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        Object.values(wxDatasetManagers).forEach((manager) => {
            manager.update();
        });
    }, [tick]);

    useEffect(() => {
        if (!wxapi || Object.keys(datasets).length === 0) return;
        if (!datasetsToExpand) return;
        setIsLoading(true);
        (async () => {
            const datasetManagers: WxDataSetManager[] = await Promise.all(
                datasetsToExpand.map((d) => {
                    return wxapi.createDatasetManager(d);
                })
            );
            setWxDatasetManagers((prev) => {
                return {
                    ...prev,
                    ...datasetManagers.reduce((acc, curr) => {
                        return { ...acc, [curr.datasetName]: curr };
                    }, {}),
                };
            });
            setIsLoading(false);
        })();
    }, [wxapi, datasetsToExpand?.join()]);

    useEffect(() => {
        const fetchAllDatasets = async () => {
            if (!wxapi) return;

            const variableIndexedDatasets = await variables.reduce(async (acc, curr) => {
                // Please fix weird awaits
                const prev = await acc;
                const allValues = await wxapi.filterDatasetsByVariableName(curr);

                return {
                    ...prev,
                    [curr]: allValues,
                };
            }, {});

            setVariableIndexedDatasets(variableIndexedDatasets);
        };
        fetchAllDatasets();
    }, [variables, wxapi?.initDone]);

    return {
        wxDatasetManagers,
        variables,
        isLoading,
        wxapi,
        datasets,
        createNewWxSource: (wxLayerOptions, frameworkOptions) => {
            return new WxTileSource(wxLayerOptions, frameworkOptions);
        },
        variableIndexedDatasets,
    };
};

export interface useWXtilesV3Return {
    wxDatasetManagers: Record<string, WxDataSetManager>;
    wxapi?: WxAPI;
    variables: string[];
    isLoading: boolean;
    datasets: Record<string, wxtilesv3Dataset>;

    // This function should not be required, but it is because of the way wxtilesv3 is currently implemented.
    // wxtiles-mbox shares state between wxapi and wxources. If you import a different wxtiles-mbox from a different project within the monorepo then the state does not match.
    // Very sad.
    createNewWxSource: (wxLayerOptions: WxLayerOptions, frameworkOptions: FrameworkOptions) => any;

    variableIndexedDatasets: Record<string, string[]>;
}

export const datasetToBbox = (dataset: wxtilesv3Dataset) => {
    if (!dataset) return undefined;
    const anyInstance = Object.values(dataset.instances)[0];
    if (!anyInstance) return undefined;
    return [
        anyInstance.boundaries.boundaries360[0].west,
        anyInstance.boundaries.boundaries360[0].south,
        anyInstance.boundaries.boundaries360[0].east,
        anyInstance.boundaries.boundaries360[0].north,
    ] as [number, number, number, number];
};
